/**
 * Kalender-Kurzuebersicht (V-01): heute als kompakte Agenda mit freien
 * Fenstern (F-50, statt eines Stundenrasters mit eigener Scrollleiste),
 * darunter die naechsten zwei Tage als kurze Liste. Nur anschauen und
 * hinklicken (V-02); dazu der Hinweis auf unbestaetigte Termine (F-29),
 * dort darf abgehakt werden.
 */
import { CalendarDays } from 'lucide-react';

import { Card } from '@/modules/dashboard/Card';
import { TerminZeilen } from '@/modules/dashboard/TerminZeilen';
import { OffeneBestaetigungen } from '@/modules/calendar/bestaetigung/OffeneBestaetigungen';
import { AgendaHeute } from '@/modules/dashboard/AgendaHeute';
import { useOccurrences } from '@/modules/calendar/hooks/useOccurrences';
import { termineAm } from '@/domain/uebersicht';
import { addDays, formatDe } from '@/lib/datetime';
import type { LocalDate, LocalDateTime } from '@/domain/types';

/** Heute plus so viele Tage (Standardwert aus V-01). */
const KALENDER_TAGE = 2;

export function KalenderBlock({ heute }: { heute: LocalDate }) {
  const occurrences = useOccurrences({
    from: `${heute}T00:00` as LocalDateTime,
    to: `${addDays(heute, KALENDER_TAGE + 1)}T00:00` as LocalDateTime,
  });
  const folgende = Array.from({ length: KALENDER_TAGE }, (_, i) => addDays(heute, i + 1));
  const name = (i: number) => (i === 0 ? 'Morgen' : i === 1 ? 'Übermorgen' : formatDe(folgende[i]!, 'EEEE'));

  return (
    <Card title="Kalender" icon={CalendarDays} to="/kalender">
      <div className="flex flex-col gap-3">
        <OffeneBestaetigungen heute={heute} />
        <AgendaHeute heute={heute} occurrences={occurrences} />
        {folgende.map((tag, i) => (
          <section key={tag} aria-label={name(i)} className="flex flex-col gap-1 border-t border-line pt-2.5">
            <h3 className="text-[11px] font-medium uppercase tracking-wide text-ink-faint">
              {name(i)} · <span className="normal-case">{formatDe(tag, 'EEEEEE dd.MM.')}</span>
            </h3>
            <TerminZeilen liste={termineAm(occurrences, tag)} leer="Keine Termine." />
          </section>
        ))}
      </div>
    </Card>
  );
}
